import React, { useState, useEffect } from 'react';
import '../styles/Modal.css';
import '../styles/VoiceCommandsModal.css';
import { PendingVoiceCommand } from '../models/types';
import { getPendingVoiceCommands, markVoiceCommandAsProcessed } from '../utils/urlUtils';
import { db } from '../services/db';

interface VoiceCommandsModalProps {
  onClose: () => void;
  onSave: () => void;
}

const VoiceCommandsModal: React.FC<VoiceCommandsModalProps> = ({ onClose, onSave }) => {
  const [pendingCommands, setPendingCommands] = useState<PendingVoiceCommand[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editedCommand, setEditedCommand] = useState<PendingVoiceCommand | null>(null);
  const [projects, setProjects] = useState<string[]>([]);
  const [tasks, setTasks] = useState<string[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load pending voice commands
        const commands = await getPendingVoiceCommands();
        setPendingCommands(commands);
        
        if (commands.length > 0) {
          setEditedCommand({ ...commands[0] });
        }
        
        // Load projects for dropdown
        const projectsData = await db.projects.toArray();
        setProjects(projectsData.map(p => p.name));
        
        // Load tasks for the first project if available
        if (commands.length > 0 && commands[0].project) {
          const project = projectsData.find(p => p.name === commands[0].project);
          if (project) {
            setTasks(project.tasks);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProjectChange = async (projectName: string) => {
    if (!editedCommand) return;
    
    setEditedCommand({ ...editedCommand, project: projectName });
    
    // Update tasks for the selected project
    const project = await db.projects.where('name').equals(projectName).first();
    if (project) {
      setTasks(project.tasks);
    } else {
      setTasks([]);
    }
  };

  const handleSave = async () => {
    if (!editedCommand || !editedCommand.id) return;
    
    try {
      // Process the command based on action
      if (editedCommand.action) {
        const currentEntry = await db.getCurrentTimeEntry();
        
        switch (editedCommand.action) {
          case 'clock-in':
            if (!currentEntry) {
              // Find or create project
              if (editedCommand.project) {
                const projects = await db.projects.where('name').equals(editedCommand.project).toArray();
                if (projects.length === 0) {
                  await db.projects.add({
                    name: editedCommand.project,
                    tasks: editedCommand.task ? [editedCommand.task] : []
                  });
                } else if (editedCommand.task && !projects[0].tasks.includes(editedCommand.task)) {
                  await db.projects.update(projects[0].id!, {
                    tasks: [...projects[0].tasks, editedCommand.task]
                  });
                }
              }

              const userName = await db.getUserName();
              
              // Create a new time entry using the new addTimeEntry method
              await db.addTimeEntry({
                name: userName || 'Anonymous',
                timeIn: editedCommand.timestamp || new Date(),
                project: editedCommand.project || 'Default Project',
                task: editedCommand.task,
                location: editedCommand.location || 'Site',
                notes: editedCommand.notes || ''
              });
            }
            break;
          
          case 'clock-out':
            if (currentEntry) {
              await db.updateTimeEntry(currentEntry.id!, {
                timeOut: new Date(),
                notes: editedCommand.notes ? `${currentEntry.notes || ''} ${editedCommand.notes}`.trim() : currentEntry.notes || ''
              });
            }
            break;
          
          case 'switch':
            if (currentEntry) {
              // Clock out current entry using the new updateTimeEntry method
              await db.updateTimeEntry(currentEntry.id!, {
                timeOut: new Date(),
                notes: editedCommand.notes ? `${currentEntry.notes || ''} ${editedCommand.notes}`.trim() : currentEntry.notes || ''
              });
              
              // Find or create project
              if (editedCommand.project) {
                const projects = await db.projects.where('name').equals(editedCommand.project).toArray();
                if (projects.length === 0) {
                  await db.projects.add({
                    name: editedCommand.project,
                    tasks: editedCommand.task ? [editedCommand.task] : []
                  });
                } else if (editedCommand.task && !projects[0].tasks.includes(editedCommand.task)) {
                  await db.projects.update(projects[0].id!, {
                    tasks: [...projects[0].tasks, editedCommand.task]
                  });
                }
              }

              // Create a new entry using the new addTimeEntry method
              await db.addTimeEntry({
                name: currentEntry.name,
                timeIn: new Date(),
                project: editedCommand.project || currentEntry.project,
                task: editedCommand.task || currentEntry.task,
                location: editedCommand.location || currentEntry.location || 'Site',
                notes: editedCommand.notes || ''
              });
            }
            break;
        }
      }
      
      // Mark as processed
      await markVoiceCommandAsProcessed(editedCommand.id);
      
      // Move to next command or close if no more
      if (currentIndex < pendingCommands.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setEditedCommand({ ...pendingCommands[currentIndex + 1] });
      } else {
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error processing voice command:', error);
    }
  };

  const handleSkip = async () => {
    if (!editedCommand || !editedCommand.id) return;
    
    try {
      // Mark as processed without taking action
      await markVoiceCommandAsProcessed(editedCommand.id);
      
      // Move to next command or close if no more
      if (currentIndex < pendingCommands.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setEditedCommand({ ...pendingCommands[currentIndex + 1] });
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Error skipping voice command:', error);
    }
  };

  if (!editedCommand) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2>Voice Commands</h2>
          <p>No pending voice commands to review.</p>
          <div className="modal-buttons">
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content voice-command-modal">
        <h2>Review Voice Command</h2>
        <p className="command-count">
          Command {currentIndex + 1} of {pendingCommands.length}
        </p>
        
        <div className="original-text">
          <label>Original Voice Text:</label>
          <div className="text-box">{editedCommand.originalText}</div>
        </div>
        
        <div className="confidence-meter">
          <label>Confidence: {Math.round(editedCommand.confidence * 100)}%</label>
          <div className="meter">
            <div 
              className="meter-fill" 
              style={{ 
                width: `${Math.round(editedCommand.confidence * 100)}%`,
                backgroundColor: editedCommand.confidence > 0.7 ? '#28a745' : 
                                editedCommand.confidence > 0.4 ? '#ffc107' : '#dc3545'
              }}
            ></div>
          </div>
        </div>
        
        <div className="form-group">
          <label>Action:</label>
          <select 
            value={editedCommand.action || ''} 
            onChange={(e) => setEditedCommand({ 
              ...editedCommand, 
              action: e.target.value as PendingVoiceCommand['action'] 
            })}
          >
            <option value="">Select Action</option>
            <option value="clock-in">Clock In</option>
            <option value="clock-out">Clock Out</option>
            <option value="switch">Switch Project</option>
          </select>
        </div>
        
        <div className="form-group">
          <label>Project:</label>
          <select 
            value={editedCommand.project || ''} 
            onChange={(e) => handleProjectChange(e.target.value)}
          >
            <option value="">Select Project</option>
            {projects.map((project, index) => (
              <option key={index} value={project}>{project}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label>Task:</label>
          <select 
            value={editedCommand.task || ''} 
            onChange={(e) => setEditedCommand({ ...editedCommand, task: e.target.value })}
          >
            <option value="">Select Task</option>
            {tasks.map((task, index) => (
              <option key={index} value={task}>{task}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label>Location:</label>
          <select 
            value={editedCommand.location || ''} 
            onChange={(e) => setEditedCommand({ ...editedCommand, location: e.target.value })}
          >
            <option value="">Select Location</option>
            <option value="Site">Site</option>
            <option value="Shop">Shop</option>
          </select>
        </div>
        
        <div className="form-group">
          <label>Notes:</label>
          <textarea 
            value={editedCommand.notes || ''} 
            onChange={(e) => setEditedCommand({ ...editedCommand, notes: e.target.value })}
          />
        </div>
        
        <div className="modal-buttons">
          <button className="skip-button" onClick={handleSkip}>Skip</button>
          <button onClick={onClose}>Cancel</button>
          <button className="save-button" onClick={handleSave}>Confirm & Save</button>
        </div>
      </div>
    </div>
  );
};

export default VoiceCommandsModal;
